import type { DataReferenceOffer, DataReferenceOffers } from '../api/client'
import { renderSample } from './DataReferencePicker'
import {
  chipLabel,
  joinProse,
  splitProse,
  type ProseSegment,
} from './dataReferenceChips'

export interface DataMentionChipsProps {
  value: string
  offers: DataReferenceOffers | null
  onChange: (value: string) => void
}

type ReferenceSuffix = { ref: string; text: string }

function referenceSuffix(segment: ProseSegment | undefined): ReferenceSuffix | null {
  if (!segment || segment.kind !== 'text') return null
  const match = segment.text.match(/^ \(([^\s()]+@v\d+)\)/)
  return match ? { ref: match[1], text: match[0] } : null
}

function offerFor(
  token: string,
  ref: string | null,
  offers: DataReferenceOffers | null,
): DataReferenceOffer | undefined {
  if (!offers) return undefined

  if (ref) {
    const set = offers.sets.find((candidate) => candidate.set === ref)
    return set?.offers.find((offer) => offer.token === token)
  }

  const matchingSets = offers.sets.filter((set) =>
    set.offers.some((offer) => offer.token === token))
  if (matchingSets.length !== 1) return undefined
  return matchingSets[0].offers.find((offer) => offer.token === token)
}

function withoutChip(
  segments: ProseSegment[],
  index: number,
  suffix: ReferenceSuffix | null,
) {
  const remaining = segments.flatMap((segment, segmentIndex) => {
    if (segmentIndex === index) return []
    if (segmentIndex === index + 1 && suffix) {
      return [{ ...segment, text: segment.text.slice(suffix.text.length) }]
    }
    return [segment]
  })

  const previous = remaining[index - 1]
  const next = remaining[index]
  if (
    previous?.kind === 'text'
    && next?.kind === 'text'
    && previous.text.endsWith(' ')
    && next.text.startsWith(' ')
  ) {
    remaining[index] = { ...next, text: next.text.slice(1) }
  }

  return joinProse(remaining)
}

export function DataMentionChips({ value, offers, onChange }: DataMentionChipsProps) {
  const segments = splitProse(value)
  const chips = segments.flatMap((segment, index) => {
    if (segment.kind !== 'chip' || !segment.token) return []
    const suffix = referenceSuffix(segments[index + 1])
    return [{ segment, index, suffix }]
  })

  if (chips.length === 0) return null

  return (
    <div data-testid="data-mention-chips" className="mt-2 flex flex-wrap gap-2">
      {chips.map(({ segment, index, suffix }) => {
        const offer = offerFor(segment.token!, suffix?.ref ?? null, offers)
        const label = chipLabel(segment.alias ?? '', segment.path ?? '')
        return (
          <span
            key={`${segment.token}-${index}`}
            data-testid="data-mention-chip"
            className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-1 text-xs text-indigo-800"
          >
            <span>{label}</span>
            {suffix && <span className="text-indigo-500">({suffix.ref})</span>}
            {offer && (
              <span data-testid="data-mention-chip-sample" className="text-gray-600">
                {renderSample(offer.sample)}
              </span>
            )}
            <button
              type="button"
              data-testid="data-mention-chip-remove"
              aria-label={`Remove ${label}`}
              onClick={() => onChange(withoutChip(segments, index, suffix))}
              className="ml-1 text-indigo-500 hover:text-indigo-900"
            >
              ×
            </button>
          </span>
        )
      })}
    </div>
  )
}
